import React, { useEffect, useRef, useState } from 'react';
import { COLOR_HEX } from './ludoBoard.js';
import { playDiceClick, playDiceLand, playDiceTick, unlockLudoAudio } from './ludoSounds.js';

const PIP_LAYOUT = {
  1: [[50, 50]],
  2: [[28, 28], [72, 72]],
  3: [[28, 28], [50, 50], [72, 72]],
  4: [[28, 28], [72, 28], [28, 72], [72, 72]],
  5: [[28, 28], [72, 28], [50, 50], [28, 72], [72, 72]],
  6: [[28, 28], [72, 28], [28, 50], [72, 50], [28, 72], [72, 72]],
};

function DiceFace({ value }) {
  const pips = PIP_LAYOUT[value] ?? [];
  return (
    <svg viewBox="0 0 100 100" className="ludo-dice-face" aria-hidden>
      {pips.map(([cx, cy], i) => (
        <circle key={i} cx={cx} cy={cy} r="9" fill="#ffffff" />
      ))}
    </svg>
  );
}

export default function Dice({ value, isRolling, onRoll, disabled, color, className = '' }) {
  const [rollingFace, setRollingFace] = useState(null);
  const wasRollingRef = useRef(false);
  const tickPlayedRef = useRef(0);

  useEffect(() => {
    if (!isRolling) {
      setRollingFace(null);
      tickPlayedRef.current = 0;
      return undefined;
    }

    unlockLudoAudio();
    playDiceClick();

    let ticks = 0;
    const maxTicks = 8;
    const interval = window.setInterval(() => {
      ticks += 1;
      setRollingFace(1 + Math.floor(Math.random() * 6));
      if (ticks >= maxTicks) {
        window.clearInterval(interval);
      }
    }, 70);

    return () => window.clearInterval(interval);
  }, [isRolling]);

  useEffect(() => {
    if (isRolling && rollingFace != null && tickPlayedRef.current !== rollingFace) {
      tickPlayedRef.current = rollingFace;
      playDiceTick();
    }
  }, [isRolling, rollingFace]);

  useEffect(() => {
    if (wasRollingRef.current && !isRolling && value != null) {
      playDiceLand();
    }
    wasRollingRef.current = isRolling;
  }, [isRolling, value]);

  const display = isRolling ? rollingFace ?? value : value;

  const handleClick = () => {
    if (disabled || isRolling) return;
    unlockLudoAudio();
    onRoll?.();
  };

  return (
    <button
      type="button"
      className={[
        'ludo-dice',
        className,
        isRolling ? 'ludo-dice-rolling' : '',
        disabled ? 'ludo-dice-disabled' : '',
      ].filter(Boolean).join(' ')}
      style={{ '--dice-accent': COLOR_HEX[color] || COLOR_HEX.blue }}
      onClick={handleClick}
      disabled={disabled}
      aria-label={display != null ? `Dice showing ${display}` : 'Roll dice'}
    >
      <span className={`ludo-dice-inner${isRolling ? ' ludo-dice-inner-rolling' : ''}`}>
        {display != null ? (
          <DiceFace value={display} />
        ) : (
          <span className="ludo-dice-empty">?</span>
        )}
      </span>
    </button>
  );
}
